/** Gate decision row — shown only on the Gate 1 / Gate 2 tabs.
 *
 * Two dispositions:
 *   [Approve]                 → POST .../approve (review_state='approved')
 *   [Continue with warnings]  → POST .../approve with
 *       edited_output={continued_with_warnings:true}
 *       (review_state='edited_approved', audited as stage.edited_approved).
 *
 * Both mark THIS gate row only (upstream agents stay notes-only drafts).
 * A free-text note flows through the separate StageCommentBox. Agent-stage
 * approval is automatic in auto-flow, so agent tabs carry no action row.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { fmtDateTime } from '@/lib/datetime';
import type { PausedStage } from '@/types/enums';
import type { AgentResult } from '@/types';

interface Props {
  jobId: string;
  stage: PausedStage;
  row: AgentResult;
  onChanged: () => void;
}

export function StageActionRow({ jobId, stage, row, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "Continue with warnings" proceeds despite flagged issues, so it takes a
  // second click to confirm — parity with the final-gate confirmation dialog.
  const [confirmWarn, setConfirmWarn] = useState(false);

  const isApproved =
    row.review_state === 'approved' || row.review_state === 'edited_approved';
  const withWarnings = Boolean(
    (row.edits_json)?.continued_with_warnings,
  );

  async function approve(body: Record<string, unknown> = {}) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/approve`, body);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed. Please retry.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/30 px-3 py-2.5">
      {isApproved && (
        <div
          className={`flex items-center gap-1.5 text-xs ${
            withWarnings
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-green-700 dark:text-green-300'
          }`}
        >
          {withWarnings ? (
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          <span>
            {withWarnings ? 'Continued with warnings' : 'Approved'}
            {row.approved_at && (
              <> at <span className="font-mono">{fmtDateTime(row.approved_at)}</span></>
            )}
          </span>
        </div>
      )}
      {!isApproved && !confirmWarn && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => { void approve({}); }}
            disabled={busy}
            className="gap-1.5 bg-green-600 text-white hover:bg-green-700"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            Approve
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => { setError(null); setConfirmWarn(true); }}
            disabled={busy}
            className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30"
          >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Continue with warnings
          </Button>
        </div>
      )}
      {!isApproved && confirmWarn && (
        <div className="space-y-2">
          <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>Proceed despite the warnings on this gate? This advances the contract.</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                void approve({ edited_output: { continued_with_warnings: true } });
              }}
              disabled={busy}
              className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              Confirm continue
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setConfirmWarn(false); }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
